import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth } from '../../AuthContext';
import { getPlatformAccess } from '../../services/platform/platformAccessService';
import { supabase } from '../../supabase';
import { useSplash } from '../../ui/splash/SplashContext';
import { releaseSignInCoverAfterPaint } from '../../ui/splash/signInCover';
import { withTimeout } from '../../utils/async/withTimeout';
import { kickSessionRevoked } from '../../utils/auth/kickSessionRevoked';
import { onPlatformAccessRecheck } from '../../utils/platform/platformAccessRecheck';
import PlatformMfaEnrollmentGate from './PlatformMfaEnrollmentGate';
import AccessStatusScreen from '../auth/AccessStatusScreen';

const MFA_GATE_TIMEOUT_MS = 12000;

const skipLoginMfa =
  process.env.NODE_ENV === 'development' &&
  (process.env.REACT_APP_SKIP_LOGIN_MFA === 'true' ||
    process.env.REACT_APP_SKIP_LOGIN_MFA === '1');

const RECHECK_DEBOUNCE_MS = 1000;

// Only a POSITIVE "this session is gone" answer may sign anyone out. Anything
// else (429, 5xx, network, offline, unknown) is "unknown" and changes nothing.
const SESSION_GONE_CODES = new Set([
  'session_not_found', 'refresh_token_not_found', 'user_not_found', 'bad_jwt', 'AuthSessionMissingError',
]);
function isSessionGoneError(error) {
  if (!error) return false;
  if (SESSION_GONE_CODES.has(error.code) || SESSION_GONE_CODES.has(error.name)) return true;
  const status = Number(error.status);
  return status === 401 || status === 403;
}

const NO_PLATFORM_ACCESS = {
  isPlatformUser: false,
  platformRole: null,
  isActive: false,
};

export default function PlatformRoute({ children }) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { sessionUser, sessionIsPasswordRecovery, loading, recovering, signOut, setAuthNotice } = useAuth();
  const [platformAccess, setPlatformAccess] = useState(NO_PLATFORM_ACCESS);
  const [platformResolved, setPlatformResolved] = useState(false);
  const [mfaChecked, setMfaChecked] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  // RD-SEC: super-admins MUST have a verified TOTP factor. `mfaRequired` only
  // catches users who already enrolled (challenge needed); this catches the
  // no-factor case and forces enrollment before the console renders.
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);
  const [mfaRecheckNonce, setMfaRecheckNonce] = useState(0);
  // Held true while the freshly-enrolled super-admin is on the recovery-codes
  // step — verify reaches AAL2 immediately, so a background checkMfa re-run
  // could otherwise clear the gate before the codes are saved. onEnrolled clears it.
  const [recoveryHold, setRecoveryHold] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const resolvePlatformAccess = async () => {
      if (!sessionUser?.id) {
        if (isMounted) {
          setPlatformAccess(NO_PLATFORM_ACCESS);
          setPlatformResolved(true);
        }
        return;
      }

      if (isMounted) {
        setPlatformResolved(false);
      }

      try {
        const access = await getPlatformAccess(sessionUser.id);
        if (isMounted) {
          setPlatformAccess(access);
          setPlatformResolved(true);
        }
      } catch (_error) {
        if (isMounted) {
          setPlatformAccess(NO_PLATFORM_ACCESS);
          setPlatformResolved(true);
        }
      }
    };

    void resolvePlatformAccess();

    return () => {
      isMounted = false;
    };
  }, [sessionUser?.id]);

  useEffect(() => {
    if (!sessionUser) {
      setMfaChecked(false);
      setMfaRequired(false);
      return;
    }
    if (loading || recovering) return;

    let isMounted = true;
    const checkMfa = async () => {
      if (skipLoginMfa) {
        if (isMounted) { setMfaChecked(true); setMfaRequired(false); setMfaEnrollmentRequired(false); }
        return;
      }
      // Bounded like ProtectedRoute: a hung request times out into the fail-closed branch.
      const timedOut = (err) => ({ data: null, error: err });
      const { data: factorsData, error: factorsError } = await withTimeout(supabase.auth.mfa.listFactors(), MFA_GATE_TIMEOUT_MS).catch(timedOut);
      const { data: aalData, error: aalError } = await withTimeout(supabase.auth.mfa.getAuthenticatorAssuranceLevel(), MFA_GATE_TIMEOUT_MS).catch(timedOut);
      if (factorsError || aalError) {
        console.error('MFA check failed in PlatformRoute:', factorsError || aalError);
        if (isMounted) { setMfaRequired(true); setMfaEnrollmentRequired(false); setMfaChecked(true); }
        return;
      }
      const hasVerifiedTotp = (factorsData?.totp ?? []).some((f) => f.status === 'verified');
      const atAal1 = Boolean(aalData?.currentLevel === 'aal1');
      const nextMfaEnrollmentRequired = !hasVerifiedTotp;
      if (isMounted) {
        // Has a verified factor but the session isn't elevated → challenge at login.
        setMfaRequired(atAal1 && hasVerifiedTotp);
        // No verified factor at all → super-admin must enroll before proceeding.
        setMfaEnrollmentRequired(nextMfaEnrollmentRequired);
        setMfaChecked(true);
      }
    };
    void checkMfa();
    return () => { isMounted = false; };
  // sessionUser?.id used as identity key; sessionUser itself needed for the null guard
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionUser?.id, loading, recovering, mfaRecheckNonce]);

  // KAN-549 fallback: a deactivated platform user whose revocation signal was
  // missed/late must still land on a proper screen, never a raw 403 on the
  // console. Server-validated getUser() first (revoked session → the same
  // "signed out" kick the revocation watch performs), then a fresh access read
  // (inactive → the existing AccessStatusScreen). Offline/transient: do nothing.
  const liveRef = useRef({});
  liveRef.current = { userId: sessionUser?.id, active: platformResolved && platformAccess.isPlatformUser && platformAccess.isActive, signOut, setAuthNotice };
  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const recheckInflightRef = useRef(null);
  const lastRecheckRef = useRef(0);
  const recheckAccess = useCallback(() => {
    if (recheckInflightRef.current) return recheckInflightRef.current;
    const { userId, active } = liveRef.current;
    if (!userId || !active) return Promise.resolve('unknown');
    const run = (async () => {
      try {
        const { error } = await supabase.auth.getUser();
        if (error && !isSessionGoneError(error)) return 'unknown';
        if (error) {
          await kickSessionRevoked({ signOut: liveRef.current.signOut, setAuthNotice: liveRef.current.setAuthNotice });
          return 'ended';
        }
        const access = await getPlatformAccess(userId);
        // getPlatformAccess folds a read error into "no access": only a
        // positively-read platform row may change what the route shows.
        if (!access?.isPlatformUser) return 'unknown';
        if (!access.isActive) {
          if (mountedRef.current) setPlatformAccess(access);
          return 'ended';
        }
        return 'active';
      } catch (_) {
        return 'unknown';
      } finally {
        recheckInflightRef.current = null;
      }
    })();
    recheckInflightRef.current = run;
    return run;
  }, []);

  useEffect(() => onPlatformAccessRecheck(() => recheckAccess()), [recheckAccess]);

  useEffect(() => {
    const onResume = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastRecheckRef.current < RECHECK_DEBOUNCE_MS) return;
      lastRecheckRef.current = now;
      void recheckAccess();
    };
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onResume);
    return () => {
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, [recheckAccess]);

  const handleInactiveSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const blocking = Boolean(loading || recovering || (sessionUser && !platformResolved));
  // Same as ProtectedRoute: the 2FA-gate check renders nothing until it
  // answers — keep the splash up through it, then release the sign-in cover.
  useSplash(blocking || (Boolean(sessionUser) && !mfaChecked));
  const guardSettled = !blocking && Boolean(sessionUser) && mfaChecked;
  useEffect(() => { if (guardSettled) releaseSignInCoverAfterPaint(); }, [guardSettled]);
  if (blocking) {
    return null;
  }

  if (!sessionUser) {
    return <Navigate to="/" replace />;
  }

  if (sessionIsPasswordRecovery) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!mfaChecked) return null;
  if (mfaRequired) return <Navigate to="/" replace />;

  if (platformResolved && !platformAccess.isPlatformUser) {
    return <Navigate to="/" replace />;
  }

  if (platformResolved && platformAccess.isPlatformUser && !platformAccess.isActive) {
    return (
      <AccessStatusScreen
        variant="account"
        email={sessionUser?.email}
        bodyOverride={intl.formatMessage({ id: 'platform.accountInactiveBody', defaultMessage: 'Your platform account is inactive. Contact your administrator.' })}
        onSignOut={handleInactiveSignOut}
      />
    );
  }

  // RD-SEC: confirmed active platform super-admin without a verified TOTP factor
  // must enroll before the console renders. Verifying a factor elevates the
  // session to AAL2; onEnrolled re-checks, the gate clears, children render.
  if (mfaEnrollmentRequired || recoveryHold) {
    return (
      <PlatformMfaEnrollmentGate
        onEnrolled={() => { setRecoveryHold(false); setMfaRecheckNonce((n) => n + 1); }}
        onSignOut={handleInactiveSignOut}
        onRecoveryHoldChange={setRecoveryHold}
      />
    );
  }

  return children;
}
